import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { Tables } from '../../../../../shared/ui/shared-tables/tables/tables';
import { LoadState } from '../../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { Loading } from '../../../../../utils/loading/loading';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { ActionInterface } from '../../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { SubcategoriesList } from '../../../../../pages/admin-dashboard/admin-subcategories/admin-subcategories.model';
import { toSubcategoryTableRows } from './subcategory-table-component.mapper';

const ACTIONS = {
  edit: { id: 1, titleEn: 'Edit Subcategory', titleAr: 'تعديل التصنيف الفرعي' },
  view: { id: 2, titleEn: 'View Subcategory Details', titleAr: 'عرض تفاصيل التصنيف الفرعي' },
  delete: { id: 3, titleEn: 'Delete Subcategory', titleAr: 'حذف التصنيف الفرعي' },
  // Tables shows id 4 for active rows and id 5 for inactive ones (via row.activationStatus).
  deactivate: { id: 4, titleEn: 'Deactivate Subcategory', titleAr: 'إلغاء تفعيل التصنيف الفرعي' },
  activate: { id: 5, titleEn: 'Activate Subcategory', titleAr: 'تفعيل التصنيف الفرعي' },
} satisfies Record<string, ActionInterface>;

@Component({
  selector: 'app-subcategory-table-component',
  imports: [Tables, Loading, ErrorData],
  templateUrl: './subcategory-table-component.html',
  styleUrl: './subcategory-table-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubcategoryTableComponent {
  private locale = inject(LOCALE_ID);

  /** Subcategories of the current page only. */
  subcategories = input<SubcategoriesList[]>([]);
  state = input<LoadState>('loading');
  currentPage = input<number>(1);
  itemsPerPage = input<number>(25);
  isFiltersApplied = input<boolean>(false);

  retry = output<void>();
  viewSubcategory = output<number>();
  editSubcategory = output<number>();
  deleteSubcategory = output<number>();
  deactivateSubcategory = output<number>();
  activateSubcategory = output<number>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  rows = computed(() =>
    toSubcategoryTableRows(
      this.subcategories(),
      this.isArabic(),
      this.locale,
      (this.currentPage() - 1) * this.itemsPerPage() + 1,
    ),
  );

  headerItems = [
    { key: 'index', label: '#' },
    { key: 'name', label: $localize`:@@adminCategoriesTableHeaderName:Subcategory Name` },
    { key: 'dateCreated', label: $localize`:@@adminCategoriesTableHeaderDateCreated:Date Created` },
    { key: 'actions', label: '', type: 'actions' },
  ];

  actions: ActionInterface[] = [ACTIONS.edit, ACTIONS.view, ACTIONS.deactivate, ACTIONS.activate, ACTIONS.delete];
  currentActions = signal<ActionInterface[]>(this.actions);

  handleAction(e: { event: Event; actionId: number; entity: { id: number } }) {
    e.event?.stopPropagation();
    switch (e.actionId) {
      case ACTIONS.edit.id:
        this.editSubcategory.emit(e.entity.id);
        break;
      case ACTIONS.view.id:
        this.viewSubcategory.emit(e.entity.id);
        break;
      case ACTIONS.delete.id:
        this.deleteSubcategory.emit(e.entity.id);
        break;
      case ACTIONS.deactivate.id:
        this.deactivateSubcategory.emit(e.entity.id);
        break;
      case ACTIONS.activate.id:
        this.activateSubcategory.emit(e.entity.id);
        break;
    }
  }
}
